import React, { useEffect, useMemo } from 'react';
import axios from 'axios';
import { Box, Grid, Card, CardContent, Typography, TextField, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import styles from './Analytics.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useErrorSetter, useSuccessSetter } from '../hooks/useUiMessages';
import { checkAuthenticatedAdmin } from '../utils/client-functions';

const PRODUCTS = ['Job Automator', 'Audio & Video Transcription AI', 'Depositions', 'Exam Simulator', 'Premium'];
const PRODUCT_DURATIONS = {
    'Job Automator': ['Per Use', 'Per Week', 'Per Month'],
    'Exam Simulator': ['Student Plan', 'Pro Plan'],
    'Audio & Video Transcription AI': ['Per Use', 'Per Week', 'Per Month'],
    'Depositions': ['Per Use', 'Per Week', 'Per Month'],
    'Premium': ['Per Use', 'Per Week', 'Per Year']
};
const ALL_DURATIONS = ['Per Use', 'Per Week', 'Per Month', 'Student Plan', 'Pro Plan', 'Per Year'];

const CouponDashboard = () => {
    const token = sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
    const navigate = useNavigate();
    const location = useLocation();
    const setError = useErrorSetter();
    const setSuccess = useSuccessSetter();
    const [currentAdminRole, setCurrentAdminRole] = React.useState('');

    // Data
    const [coupons, setCoupons] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const [limit, setLimit] = React.useState(50);
    const [loading, setLoading] = React.useState(false);
    const [stats, setStats] = React.useState({ filteredTotal: 0, activeCount: 0, expiredCount: 0, maxedCount: 0, newInRange: 0 });
    const [typing, setTyping] = React.useState(null);

    // Filters
    const [productName, setProductName] = React.useState('');
    const [subType, setSubType] = React.useState('');
    const [status, setStatus] = React.useState('');
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [query, setQuery] = React.useState('');
    const [sortBy, setSortBy] = React.useState('createdAt');
    const [sortDir, setSortDir] = React.useState('desc');

    // Create dialog
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [form, setForm] = React.useState({ productName: '', subDuration: '', discountPercentage: 0, createdUseCount: 1, codeLength: 12, couponCode: '', expiration: '' });

    // Hydrate from URL
    useEffect(() => {
        (async () => {
            try { await checkAuthenticatedAdmin(); } catch { return navigate('/'); }
        })();
        // fetch current admin role for gating
        (async () => {
            try {
                const resp = await axios.get('/origin/get-current-admin', { headers: { 'x-access-token': token } });
                setCurrentAdminRole(resp?.data?.role || '');
            } catch {}
        })();
        const params = new URLSearchParams(location.search);
        setPage(parseInt(params.get('page') || '1', 10));
        setLimit(parseInt(params.get('limit') || '50', 10));
        setProductName(params.get('productName') || '');
        setSubType(params.get('subType') || '');
        setStatus(params.get('status') || '');
        setStartDate(params.get('startDate') || '');
        setEndDate(params.get('endDate') || '');
        setQuery(params.get('q') || '');
        setSortBy(params.get('sortBy') || 'createdAt');
        setSortDir(params.get('sortDir') || 'desc');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync URL
    useEffect(() => {
        const p = new URLSearchParams();
        p.set('page', String(page));
        p.set('limit', String(limit));
        if (productName) p.set('productName', productName);
        if (subType) p.set('subType', subType);
        if (status) p.set('status', status);
        if (startDate) p.set('startDate', startDate);
        if (endDate) p.set('endDate', endDate);
        if (query) p.set('q', query);
        if (sortBy) p.set('sortBy', sortBy);
        if (sortDir) p.set('sortDir', sortDir);
        const newUrl = `${location.pathname}?${p.toString()}`;
        if (newUrl !== window.location.pathname + window.location.search) {
            window.history.replaceState(null, '', newUrl);
        }
    }, [page, limit, productName, subType, status, startDate, endDate, query, sortBy, sortDir, location.pathname]);

    useEffect(() => {
        const fetchCoupons = async () => {
            try { await checkAuthenticatedAdmin(); } catch { return navigate('/'); }
            try {
                setLoading(true);
                const p = new URLSearchParams();
                p.set('page', String(page));
                p.set('limit', String(limit));
                if (productName) p.set('productName', productName);
                if (subType) p.set('subType', subType);
                if (status) p.set('status', status);
                if (startDate) p.set('startDate', startDate);
                if (endDate) p.set('endDate', endDate);
                if (query) p.set('q', query);
                if (sortBy) p.set('sortBy', sortBy);
                if (sortDir) p.set('sortDir', sortDir);
                const resp = await axios.get(`/origin/get-all-coupons?${p.toString()}`, { headers: { 'x-access-token': token } });
                if (resp.status === 200) {
                    setCoupons(resp.data.coupons || []);
                    setTotal(resp.data.total || 0);
                }
            } catch (e) {
                setError(e?.response?.data?.message || 'Failed to fetch coupons');
            } finally {
                setLoading(false);
            }
        };
        fetchCoupons();
    }, [page, limit, productName, subType, status, startDate, endDate, query, sortBy, sortDir, token, navigate, setError]);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const p = new URLSearchParams();
                if (productName) p.set('productName', productName);
                if (subType) p.set('subType', subType);
                if (startDate) p.set('startDate', startDate);
                if (endDate) p.set('endDate', endDate);
                if (query) p.set('q', query);
                const resp = await axios.get(`/origin/get-coupons-stats?${p.toString()}`, { headers: { 'x-access-token': token } });
                setStats(resp?.data || { filteredTotal: 0, activeCount: 0, expiredCount: 0, maxedCount: 0, newInRange: 0 });
            } catch {}
        };
        fetchStats();
    }, [productName, subType, startDate, endDate, query, token]);

    const onSortChange = (key) => {
        setSortDir((prev) => (sortBy === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
        setSortBy(key);
        setPage(1);
    };

    const resetFilters = () => {
        setProductName('');
        setSubType('');
        setStatus('');
        setStartDate('');
        setEndDate('');
        setQuery('');
        setPage(1);
    };

    const exportCSV = () => {
        const cols = ['code','productName','subType','discountPercentage','allotedUseCount','currentUseCount','createdAt','expiration'];
        const escapeCell = (v) => {
            if (v === null || v === undefined) return '';
            const s = String(v).replace(/"/g, '""');
            return /[",\n]/.test(s) ? '"' + s + '"' : s;
        };
        const header = cols.join(',');
        const rows = coupons.map(c => cols.map(k => escapeCell(c[k])).join(','));
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `coupons_page${page}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportAllFilteredCSV = async () => {
        try {
            const p = new URLSearchParams();
            p.set('page', '1');
            p.set('limit', '5000');
            if (productName) p.set('productName', productName);
            if (subType) p.set('subType', subType);
            if (status) p.set('status', status);
            if (startDate) p.set('startDate', startDate);
            if (endDate) p.set('endDate', endDate);
            if (query) p.set('q', query);
            if (sortBy) p.set('sortBy', sortBy);
            if (sortDir) p.set('sortDir', sortDir);
            const resp = await axios.get(`/origin/get-all-coupons?${p.toString()}`, { headers: { 'x-access-token': token } });
            const items = resp?.data?.coupons || [];
            const cols = ['code','productName','subType','discountPercentage','allotedUseCount','currentUseCount','createdAt','expiration'];
            const escapeCell = (v) => {
                if (v === null || v === undefined) return '';
                const s = String(v).replace(/"/g, '""');
                return /[",\n]/.test(s) ? '"' + s + '"' : s;
            };
            const header = cols.join(',');
            const rows = items.map(c => cols.map(k => escapeCell(c[k])).join(','));
            const csv = [header, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `coupons_all_${new Date().toISOString().slice(0,10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setError('Failed to export all');
        }
    };

    const openCreate = () => {
        setForm({ productName: '', subDuration: '', discountPercentage: 0, createdUseCount: 1, codeLength: 12, couponCode: '', expiration: new Date(Date.now() + 2592000000).toISOString().split('T')[0] });
        setDialogOpen(true);
    };

    const submitCreateCoupon = async () => {
        if (currentAdminRole !== 'superadmin') {
            setError('Not authorized');
            return;
        }
        const allowed = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const okChars = (s) => (s || '').split('').every(ch => allowed.includes(ch));
        if (!form.productName || !form.subDuration || !form.expiration) return setError('Fill required fields');
        if (form.discountPercentage == null || Number(form.discountPercentage) <= 4) return setError('Discount must be > 4');
        if (form.couponCode && (!okChars(form.couponCode) || form.couponCode.length < 8 || form.couponCode.length > 20)) return setError('Coupon code must be 8-20 chars [A-Za-z0-9]');
        try {
            const resp = await axios.post('/origin/create-coupon', form, { headers: { 'x-access-token': token } });
            if (resp.status === 201) {
                setDialogOpen(false);
                setSuccess(`Coupon ${resp?.data?.newCoupon?.code} created`);
                // Refresh list
                const p = new URLSearchParams(window.location.search);
                const list = await axios.get(`/origin/get-all-coupons?${p.toString()}`, { headers: { 'x-access-token': token } });
                setCoupons(list?.data?.coupons || []);
                setTotal(list?.data?.total || 0);
            }
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to create coupon');
        }
    };

    const deleteCoupon = async (code) => {
        if (currentAdminRole !== 'superadmin') {
            setError('Not authorized');
            return;
        }
        try {
            if (!window.confirm(`Delete coupon ${code}?`)) return;
            const resp = await axios.post('/origin/delete-coupon', { code }, { headers: { 'x-access-token': token } });
            if (resp.status === 200) {
                setCoupons(prev => prev.filter(c => c.code !== code));
            }
        } catch (e) {
            setError(e?.response?.data || 'Failed to delete coupon');
        }
    };

    const kpis = useMemo(() => ({
        loaded: coupons.length,
    }), [coupons.length]);

    return (
        <div className={styles.analyticsContainer}>
            <AdminSideMenu />
            <div className={styles.analyticsContent}>
                <div className={styles.analyticsHeader}>
                    <div className={styles.headerLeft}>
                        <Typography variant="h4" className={styles.pageTitle}>Coupons</Typography>
                        <Typography variant="body2" className={styles.pageSubtitle}>Create, filter and manage coupons</Typography>
                    </div>
                </div>

                <Box sx={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <TextField size="small" label="Search (code/product/duration)" value={query} onChange={(e) => { const v = e.target.value; setQuery(v); setPage(1); clearTimeout(typing); setTyping(setTimeout(()=>setQuery(v), 300)); }} />
                    <Select size="small" displayEmpty value={productName} onChange={(e) => { setProductName(e.target.value); setPage(1); }}>
                        <MenuItem value=""><em>Product: Any</em></MenuItem>
                        {PRODUCTS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                    <Select size="small" displayEmpty value={subType} onChange={(e) => { setSubType(e.target.value); setPage(1); }}>
                        <MenuItem value=""><em>Duration: Any</em></MenuItem>
                        {ALL_DURATIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </Select>
                    <Select size="small" displayEmpty value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                        <MenuItem value=""><em>Status: Any</em></MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                        <MenuItem value="maxed">Maxed</MenuItem>
                    </Select>
                    <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
                    <TextField size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={resetFilters}>Reset</Button>
                        {currentAdminRole === 'superadmin' && (
                            <>
                                <Button variant="outlined" onClick={openCreate}>Create</Button>
                                <Button variant="outlined" onClick={exportAllFilteredCSV}>Export All</Button>
                                <Button variant="contained" onClick={exportCSV}>Export Page</Button>
                            </>
                        )}
                    </Box>
                </Box>

                <Grid container spacing={2} className={styles.kpiContainer}>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                <Typography className={styles.kpiLabel}>Loaded</Typography>
                                <Typography className={styles.kpiValue}>{kpis.loaded}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                <Typography className={styles.kpiLabel}>Total (filtered)</Typography>
                                <Typography className={styles.kpiValue}>{stats.filteredTotal || total}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                <Typography className={styles.kpiLabel}>Active</Typography>
                                <Typography className={styles.kpiValue}>{stats.activeCount || 0}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                <Typography className={styles.kpiLabel}>Expired</Typography>
                                <Typography className={styles.kpiValue}>{stats.expiredCount || 0}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                <Typography className={styles.kpiLabel}>Maxed</Typography>
                                <Typography className={styles.kpiValue}>{stats.maxedCount || 0}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                <Typography className={styles.kpiLabel}>New (in range)</Typography>
                                <Typography className={styles.kpiValue}>{stats.newInRange || 0}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box className={styles.chartContainer} sx={{ p: 0 }}>
                    <CouponsTable coupons={coupons} sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} onDelete={deleteCoupon} canManage={currentAdminRole === 'superadmin'} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                    <Button disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
                    <Typography variant="body2">Page {page}</Typography>
                    <Button disabled={page * limit >= total || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
                    <Select size="small" value={String(limit)} onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}>
                        {[25, 50, 100, 200].map(n => <MenuItem key={n} value={String(n)}>{n}/page</MenuItem>)}
                    </Select>
                </Box>

                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                    <DialogTitle>Create Coupon</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Select displayEmpty value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })}>
                            <MenuItem value=""><em>Product</em></MenuItem>
                            {PRODUCTS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                        <Select 
                            displayEmpty 
                            value={form.subDuration} 
                            onChange={(e) => setForm({ ...form, subDuration: e.target.value })}
                            disabled={!form.productName}
                        >
                            <MenuItem value=""><em>Duration</em></MenuItem>
                            {(PRODUCT_DURATIONS[form.productName] || []).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                        </Select>
                        <TextField label="Discount %" type="number" value={form.discountPercentage} onChange={(e) => setForm({ ...form, discountPercentage: Number(e.target.value) })} />
                        <TextField label="Allotted Count" type="number" value={form.createdUseCount} onChange={(e) => setForm({ ...form, createdUseCount: Number(e.target.value) })} />
                        <TextField label="Code Length" type="number" value={form.codeLength} onChange={(e) => setForm({ ...form, codeLength: Number(e.target.value) })} />
                        <TextField label="Coupon Code (optional)" value={form.couponCode} onChange={(e) => setForm({ ...form, couponCode: e.target.value })} />
                        <TextField label="Expiration" type="date" InputLabelProps={{ shrink: true }} value={form.expiration} onChange={(e) => setForm({ ...form, expiration: e.target.value })} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={submitCreateCoupon} disabled={currentAdminRole !== 'superadmin'} title={currentAdminRole !== 'superadmin' ? 'Only superadmin can create coupons' : undefined}>Create</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
};

const CouponsTable = ({ coupons, sortBy, sortDir, onSortChange, onDelete, canManage }) => {
    const cols = [
        { key: 'code', label: 'Code', width: 180 },
        { key: 'productName', label: 'Product', width: 200 },
        { key: 'subType', label: 'Duration', width: 140 },
        { key: 'discountPercentage', label: 'Discount %', width: 120 },
        { key: 'allotedUseCount', label: 'Allotted', width: 120 },
        { key: 'currentUseCount', label: 'Used', width: 100 },
        { key: 'createdAt', label: 'Created', width: 180 },
        { key: 'expiration', label: 'Expires', width: 180 },
    ];
    const arrow = (k) => (sortBy === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
    const statusOf = (c) => {
        const now = Date.now();
        const exp = new Date(c.expiration).getTime();
        if (exp <= now) return 'Expired';
        if ((c.currentUseCount || 0) >= (c.allotedUseCount || 0)) return 'Maxed';
        return 'Active';
    };
    return (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate' }}>
                <thead>
                    <tr>
                        {cols.map(c => (
                            <th key={c.key} style={{ textAlign: 'left', background: '#c0d1d457', padding: '12px', cursor: 'pointer', width: c.width }} onClick={() => onSortChange && onSortChange(c.key)}>
                                {c.label}{arrow(c.key)}
                            </th>
                        ))}
                        <th style={{ background: '#c0d1d457', padding: '12px', width: 120 }}>Status</th>
                        {canManage && <th style={{ background: '#c0d1d457', padding: '12px', width: 100 }}>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {coupons.map((c, idx) => (
                        <tr key={c._id || idx}>
                            {cols.map(col => (
                                <td key={col.key} style={{ padding: '10px' }}>{String(c[col.key] ?? '')}</td>
                            ))}
                            <td style={{ padding: '10px' }}>{statusOf(c)}</td>
                            {canManage && (
                                <td style={{ padding: '10px' }}>
                                    <button onClick={() => onDelete && onDelete(c.code)} style={{ color: '#b00020' }}>Delete</button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </Box>
    );
};

export default CouponDashboard;
